// SINGLE SOURCE for "which service is this job / quote?" — every bizSAFE-specific screen asks this so client
// Internal Audit (third-party ISO internal audits for clients) never leaks into bizSAFE logic.
//
// RULE for protections: always write them as "leave out Internal Audit" (isIA), never "only bizSAFE". SHMS jobs
// are part of today's bizSAFE screens/reminders, so "only bizSAFE" would silently change existing behaviour.
//
// Client Internal Audit uses the existing registry type 'ISO Internal Audit' (S009, category ISO).
// NOT to be confused with CCIS's own internal audit (pages/InternalAudit.js, "CCIS Internal Audit").
import { resolveScopeCategory } from './supabase'

export const isIA = (auditType) => resolveScopeCategory(auditType) === 'ISO'
export const isIAQuote = (quote) => isIA(quote?.audit_type)

// MOM Overtime Exemption (OTE) Risk Register Endorsement — its own service (Oct 2026). Matched by its EXACT name and
// deliberately NOT in AUDIT_TYPES / SCOPE_REGISTRY (that would put it in bizSAFE dropdowns and auditor scope checks).
// The name avoids every substring the database matches on (ISO, internal audit, risk management, bizSAFE, SHMS).
export const OTE_TYPE = 'OTE Endorsement Audit'
export const isOTE = (auditType) => String(auditType || '').trim().toLowerCase() === OTE_TYPE.toLowerCase()
export const isOTEQuote = (quote) => isOTE(quote?.audit_type)
// "Not bizSAFE work" — Internal Audit or OTE. bizSAFE-only protections leave these out (same rule as before for IA).
export const isOtherService = (auditType) => isIA(auditType) || isOTE(auditType)
// Which service tab a job / quote belongs to: 'bizsafe' | 'ia' | 'ote'.
export const serviceOf = (auditType) => (isOTE(auditType) ? 'ote' : isIA(auditType) ? 'ia' : 'bizsafe')

// Today's date in Singapore (YYYY-MM-DD) — never the UTC date, which is still "yesterday" before 8am SGT.
export const sgToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Singapore' })

// The standards a client can be certified to (edition matters — the checklist/plan is picked from these).
export const IA_STANDARDS = ['ISO 9001:2015', 'ISO 14001:2015', 'ISO 45001:2018', 'ISO/IEC 27001:2022',
  'ISO/IEC 42001:2023', 'ISO 22301:2019', 'SS 651:2019', 'ISO/IEC 17025:2017']
// The tick-box list for a record: the standard list plus anything already on the record that isn't in it (e.g. a
// standard typed under "Other" on the quote), so ticking a box never drops it.
export const iaStandardOptions = (current) => [...IA_STANDARDS, ...(current || []).filter(s => s && !IA_STANDARDS.includes(s))]

// "ISO 9001:2015, ISO 14001:2015 & ISO 45001:2018"
export function standardsLabel(standards) {
  const s = (standards || []).filter(Boolean)
  if (s.length <= 1) return s[0] || ''
  return `${s.slice(0, -1).join(', ')} & ${s[s.length - 1]}`
}

// Internal Audit job stages (jobs.ia_stage) — kept OUT of jobs.status so bizSAFE screens never see new values.
export const IA_STAGE_LABEL = {
  returned: 'Returned to auditor',
  awaiting_payment: 'Awaiting payment',
  sending: 'Sending report',
  awaiting_closeout: 'Awaiting close-out',
  done: 'Report sent',
}

// One status word for an Internal Audit job (Jobs list, My Audits, Tracker).
export function iaJobStatus(j) {
  if (j.status === 'Cancelled') return 'Cancelled'
  if (j.ia_stage === 'done' || (!j.ia_stage && j.status === 'Completed')) return 'Completed'
  if (j.ia_stage && IA_STAGE_LABEL[j.ia_stage]) return IA_STAGE_LABEL[j.ia_stage]
  if (!j.audit_date) return 'Awaiting scheduling'
  if (!j.ia_plan_token) return 'Plan not sent'
  if (j.audit_date <= sgToday()) return 'Audit in progress'
  return 'Scheduled'
}

// OTE job stages (jobs.ote_stage) — also kept OUT of jobs.status. null = scheduled.
export const OTE_STAGE_LABEL = {
  scheduled: 'Scheduled',
  confirmed: 'Confirmation sent',
  in_progress: 'Job in progress',
  awaiting_closeout: 'Waiting for client',
  signing: 'Signing',
  ready_to_send: 'Ready to send endorsement',
  awaiting_payment: 'Awaiting payment',
  sending: 'Sending endorsement',
  completed: 'Endorsement sent',
  not_endorsed: 'Not endorsed',
  closed: 'Closed — not endorsed',
  void: 'Void',
}
// One status word for an OTE job (Jobs list, My Audits).
export function oteJobStatus(j) {
  if (j.ote_stage === 'void') return 'Void'
  if (j.status === 'Cancelled') return 'Cancelled'
  if (j.ote_stage && OTE_STAGE_LABEL[j.ote_stage]) return OTE_STAGE_LABEL[j.ote_stage]
  if (!j.audit_date) return 'Awaiting scheduling'
  return 'Scheduled'
}
// Finished = nothing more to do on the job (drops off My Audits; counts as done on the Jobs list).
export const oteFinished = (j) => ['completed', 'closed', 'void'].includes(j.ote_stage) || j.status === 'Cancelled'
